import React, { useState, useEffect } from 'react'
import { ArrowRight, Star, Shield, Users, MapPin, Square, Phone, Mail, Clock, Target, Award, Home, Zap, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { propertyAPI } from '../services/api'
import useIntersectionObserver from '../hooks/useIntersectionObserver'

function HomePage() {
  const [properties, setProperties] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedProperty, setSelectedProperty] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [heroVisible, setHeroVisible] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  // Animasyon için intersection observer'lar
  const [featuresRef, featuresVisible] = useIntersectionObserver()
  const [propertiesRef, propertiesVisible] = useIntersectionObserver()
  const [aboutRef, aboutVisible] = useIntersectionObserver()
  const [contactRef, contactVisible] = useIntersectionObserver()
  const [storyRef, storyVisible] = useIntersectionObserver()
  const [valuesRef, valuesVisible] = useIntersectionObserver()
  const [statsRef, statsVisible] = useIntersectionObserver()

  // Hakkımızda bölümü için dönen görseller
  const officeImages = [
    '/foto/ofis1.jpg',
    '/foto/ofis2.jpg',
    '/foto/ofis3.jpg'
  ]

  // Hero section için animasyon
  useEffect(() => {
    const timer = setTimeout(() => {
      setHeroVisible(true)
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  // Hakkımızda bölümü görsel döngüsü
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % officeImages.length
      )
    }, 3000)
    return () => clearInterval(interval)
  }, [])

  const features = [
    {
      icon: Star,
      title: 'Premium Hizmet',
      description: '20 yıllık deneyimimizle size en iyi hizmeti sunuyoruz.'
    },
    {
      icon: Shield,
      title: 'Güvenilir İşlemler',
      description: 'Tüm emlak işlemlerinizi güvenle gerçekleştirin.'
    },
    {
      icon: Users,
      title: 'Uzman Ekip',
      description: 'Sertifikalı danışmanlarımızla çalışın.'
    }
  ]

  const values = [
    {
      icon: Target,
      title: 'Müşteri Odaklılık',
      description: 'Müşterilerimizin ihtiyaçlarını önceleyerek, onlara en uygun çözümleri sunmaya odaklanıyoruz.'
    },
    {
      icon: Award,
      title: 'Profesyonellik',
      description: 'Sektördeki deneyimimiz ve uzman kadromuzla her zaman en yüksek standartları koruyoruz.'
    },
    {
      icon: Shield,
      title: 'Güvenilirlik',
      description: 'Tüm işlemlerimizde şeffaflık ve dürüstlük ilkesiyle hareket eder, güven veririz.'
    },
    {
      icon: Users,
      title: 'Ekip Çalışması',
      description: 'Güçlü ekip ruhumuzla birlikte çalışarak en iyi sonuçları elde ediyoruz.'
    }
  ]

  const contactInfo = [
    {
      icon: Phone,
      title: 'Telefon',
      details: ['+90 (533) 400 9199'],
      description: 'Hafta içi 09:00 - 18:00'
    },
    {
      icon: Mail,
      title: 'E-posta',
      details: ['alicnszz1983@gmail.com'],
      description: 'Size en kısa sürede dönüş yapıyoruz'
    },
    {
      icon: MapPin,
      title: 'Adres',
      details: ['OSB 2.Organize Sanayi Bolgesi', 'Şehitkamil / Gaziantep'],
      description: 'Ziyaret saatleri: 09:00 - 18:00'
    },
    {
      icon: Clock,
      title: 'Çalışma Saatleri',
      details: ['Pazartesi - Cumartesi: 08:00 - 18:00'],
      description: 'Pazar kapalı'
    }
  ]

  // Backend'den ilanları getir
  useEffect(() => {
    loadProperties()
  }, [])

  const loadProperties = async () => {
    try {
      setLoading(true)
      const response = await propertyAPI.getAll()
      setProperties(response.data) // Tüm ilanlar
    } catch (err) {
      console.error('İlanlar yüklenirken hata oluştu:', err)
      setProperties([])
    } finally {
      setLoading(false)
    }
  }

  const PropertyCard = ({ property }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    
    // İmaj dizisini oluştur ve URL'leri düzelt
    const processImages = (imgs) => {
      if (!imgs || imgs.length === 0) {
        return [property.image || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=500']
      }
      return imgs.map(img => {
        if (typeof img === 'string') {
          // Eğer zaten tam URL ise olduğu gibi döndür
          if (img.startsWith('http')) return img
          // Backend'den gelen dosya adıysa tam URL'e çevir
          const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          const backendUrl = isLocalhost 
            ? `http://localhost:5000` 
            : `https://organizem-backend.onrender.com`
          return `${backendUrl}/uploads/${img}`
        }
        return img
      })
    }
    
    const images = processImages(property.images)
    const currentImage = images[currentImageIndex]

    const nextImage = (e) => {
      e.stopPropagation()
      setCurrentImageIndex((prev) => (prev + 1) % images.length)
    }

    const prevImage = (e) => {
      e.stopPropagation()
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
    }

    const handleCardClick = () => {
      setSelectedProperty(property)
      setShowModal(true)
    }

    return (
      <div 
        onClick={handleCardClick}
        className="bg-white border border-gray-200 hover:border-primary-300 overflow-hidden transition-all duration-200 cursor-pointer"
      >
        <div className="relative">
          <img 
            src={currentImage || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=500'} 
            alt={property.title}
            className="w-full h-64 object-cover"
          />
          
          {/* Image Navigation */}
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-600 p-2 transition-colors duration-200"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-600 p-2 transition-colors duration-200"
              >
                <ChevronRight size={16} />
              </button>
              
              {/* Image indicators */}
              <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                {images.map((image, index) => (
                  <div
                    key={`${property.id}-image-${index}`}
                    className={`w-2 h-2 rounded-full ${
                      index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          <div className="absolute top-4 left-4">
            <span className={`px-3 py-1 text-xs font-light ${
              property.type === 'Satılık' 
                ? 'bg-white text-green-700 border border-green-200' 
                : 'bg-white text-blue-700 border border-blue-200'
            }`}>
              {property.type}
            </span>
          </div>
          {property.featured && (
            <div className="absolute top-4 right-4">
              <span className="bg-white text-primary-700 px-3 py-1 text-xs font-light border border-primary-200">
                Öne Çıkan
              </span>
            </div>
          )}
        </div>
        
        <div className="p-6">
          <h3 className="text-lg font-light text-gray-800 mb-2 tracking-wide">
            {property.title}
          </h3>
          <div className="flex items-center text-gray-500 mb-4">
            <MapPin size={14} className="mr-2" />
            <span className="text-sm font-light">{property.location}</span>
          </div>
          
          <div className="space-y-2 mb-4 text-sm">
            <div className="flex items-center justify-between py-1 border-b border-gray-100">
              <span className="flex items-center text-gray-600 font-light">
                <Home size={14} className="mr-2" />
                Kapalı Alan:
              </span>
              <span className="font-light text-gray-800">{property.closedArea}m²</span>
            </div>
            <div className="flex items-center justify-between py-1 border-b border-gray-100">
              <span className="flex items-center text-gray-600 font-light">
                <Square size={14} className="mr-2" />
                Açık Alan:
              </span>
              <span className="font-light text-gray-800">{property.openArea}m²</span>
            </div>
            <div className="flex items-center justify-between py-1 border-b border-gray-100">
              <span className="flex items-center text-gray-600 font-light">
                <Zap size={14} className="mr-2" />
                Elektrik Gücü:
              </span>
              <span className="font-light text-gray-800">{property.electricPower}KW</span>
            </div>
          </div>
          
          {property.description && (
            <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed font-light">
              {property.description}
            </p>
          )}
          
          <div className="flex items-center justify-between pt-2">
            <div>
              <span className="text-xl font-light text-primary-700 tracking-wide">
                {parseInt(property.price).toLocaleString()} {property.currency || 'TL'}
              </span>
            </div>
            <div className="text-xs text-gray-400 font-light">
              Detaylar için tıklayın
            </div>
          </div>
        </div>
      </div>
    )
  }

  const PropertyModal = () => {
    const [modalImageIndex, setModalImageIndex] = useState(0)
    
    if (!selectedProperty || !showModal) return null

    // Modal için resim işleme fonksiyonu
    const processModalImages = (imgs) => {
      if (!imgs || imgs.length === 0) {
        return [selectedProperty.image || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=500']
      }
      return imgs.map(img => {
        if (typeof img === 'string') {
          if (img.startsWith('http')) return img
          const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          const backendUrl = isLocalhost 
            ? `http://localhost:5000` 
            : `https://organizem-backend.onrender.com`
          return `${backendUrl}/uploads/${img}`
        }
        return img
      })
    }

    const modalImages = processModalImages(selectedProperty.images)
    const currentModalImage = modalImages[modalImageIndex]

    const nextModalImage = () => {
      setModalImageIndex((prev) => (prev + 1) % modalImages.length)
    }

    const prevModalImage = () => {
      setModalImageIndex((prev) => (prev - 1 + modalImages.length) % modalImages.length)
    }

    // Modal açıldığında resim indexini sıfırla
    React.useEffect(() => {
      setModalImageIndex(0)
    }, [selectedProperty])

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-2xl font-thin text-gray-800 tracking-wide">
              İlan Detayları
            </h3>
            <button
              onClick={() => setShowModal(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2"
            >
              <X size={24} />
            </button>
          </div>

          {/* Modal Content */}
          <div className="p-6">
            {/* Property Image with Navigation */}
            <div className="relative mb-6">
              <img 
                src={currentModalImage} 
                alt={selectedProperty.title}
                className="w-full h-64 object-cover rounded-lg"
              />
              
              {/* Image Navigation for Modal */}
              {modalImages.length > 1 && (
                <>
                  <button
                    onClick={prevModalImage}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={nextModalImage}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                  
                  {/* Image indicators for Modal */}
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                    {modalImages.map((_, index) => (
                      <button
                        key={`modal-${selectedProperty.id}-image-${index}`}
                        onClick={() => setModalImageIndex(index)}
                        className={`w-3 h-3 rounded-full transition-colors ${
                          index === modalImageIndex ? 'bg-white' : 'bg-white/50 hover:bg-white/80'
                        }`}
                      />
                    ))}
                  </div>
                  
                  {/* Image counter */}
                  <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-sm">
                    {modalImageIndex + 1} / {modalImages.length}
                  </div>
                </>
              )}

              <div className="absolute top-4 left-4">
                <span className={`px-3 py-1 text-xs font-light ${
                  selectedProperty.type === 'Satılık' 
                    ? 'bg-white text-green-700 border border-green-200' 
                    : 'bg-white text-blue-700 border border-blue-200'
                }`}>
                  {selectedProperty.type}
                </span>
              </div>
              {selectedProperty.featured && (
                <div className="absolute top-4 right-4">
                  <span className="bg-white text-primary-700 px-3 py-1 text-xs font-light border border-primary-200">
                    Öne Çıkan
                  </span>
                </div>
              )}
            </div>

            {/* Property Details */}
            <div className="space-y-4">
              <div>
                <h4 className="text-2xl font-bold text-gray-900 mb-2">
                  {selectedProperty.title}
                </h4>
                <div className="flex items-center text-gray-600 mb-4">
                  <MapPin size={16} className="mr-1" />
                  <span>{selectedProperty.location}</span>
                </div>
              </div>

              {/* Price */}
              <div className="bg-beige-50 p-6 border border-gray-200">
                <div className="text-3xl font-light text-primary-700 tracking-wide">
                  {parseInt(selectedProperty.price).toLocaleString()} {selectedProperty.currency || 'TL'}
                </div>
              </div>

              {/* Technical Details */}
              <div className="bg-white p-6 border border-gray-200">
                <h5 className="font-light text-gray-800 mb-4 tracking-wide">Teknik Özellikler</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center text-gray-600">
                      <Home size={16} className="mr-2" />
                      Kapalı Alan:
                    </span>
                    <span className="font-medium">{selectedProperty.closedArea}m²</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center text-gray-600">
                      <Square size={16} className="mr-2" />
                      Açık Alan:
                    </span>
                    <span className="font-medium">{selectedProperty.openArea}m²</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center text-gray-600">
                      <Zap size={16} className="mr-2" />
                      Elektrik Gücü:
                    </span>
                    <span className="font-medium">{selectedProperty.electricPower}KW</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedProperty.description && (
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Açıklama</h5>
                  <p className="text-gray-600 leading-relaxed">
                    {selectedProperty.description}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-200 bg-white">
            <button
              onClick={() => setShowModal(false)}
              className="btn-secondary"
            >
              Kapat
            </button>
            <Link to="/iletisim" className="btn-primary">
              İletişime Geç
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
            {/* Hero Section with Background Video */}
      <section className="relative min-h-screen flex items-center text-white overflow-hidden">
        {/* Background Video */}
        <video 
          autoPlay 
          muted 
          loop 
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/video/OSB DRON ÇEKİM(1) (online-video-cutter.com) (2) (2).mp4" type="video/mp4" />
        </video>
        
        {/* Light overlay for better text readability */}
        <div className="absolute inset-0 bg-black/40"></div>
        
        {/* Content */}
        <div className="relative container-custom py-32 lg:py-40 z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className={`text-4xl lg:text-6xl font-thin text-white mb-8 leading-tight tracking-wide drop-shadow-lg fade-in ${heroVisible ? 'visible' : ''}`}>
              <span className="text-primary-300">Siz Düşünün</span>
              <br />
              Biz Organize Edelim
            </h1>
            
            <div className={`flex flex-col sm:flex-row gap-4 justify-center slide-up ${heroVisible ? 'visible' : ''}`} style={{ transitionDelay: '0.3s' }}>
              <Link to="/ilanlar" className="btn-primary inline-flex items-center justify-center">
                İlanları Keşfedin
                <ArrowRight className="ml-2" size={16} />
              </Link>
              <a 
                href="tel:+905334009199" 
                className="bg-white hover:bg-gray-50 text-primary-800 font-semibold py-3 px-8 rounded-sm border-2 border-primary-600 hover:border-primary-700 transition-all duration-200 focus:outline-none inline-flex items-center justify-center shadow-lg"
              >
                <Phone className="mr-2" size={16} />
                0533 400 9199
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className={`py-20 bg-white fade-in ${featuresVisible ? 'visible' : ''}`}>
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-thin text-gray-800 mb-4 tracking-wide">
              Neden Bizi Tercih Etmelisiniz?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto font-light">
              Gayrimenkul sektöründeki deneyimimiz ve müşteri odaklı yaklaşımımızla 
              size en iyi hizmeti sunuyoruz.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <div 
                  key={index} 
                  className={`text-center bg-white p-8 border border-gray-200 hover:border-primary-300 slide-up ${featuresVisible ? 'visible' : ''}`}
                  style={{ transitionDelay: `${index * 0.2}s` }}
                >
                  <div className="w-16 h-16 mx-auto mb-6 bg-beige-100 flex items-center justify-center">
                    <Icon className="text-primary-600" size={24} />
                  </div>
                  <h3 className="text-xl font-light text-gray-800 mb-4 tracking-wide">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed font-light">
                    {feature.description}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* İlanlarım Section */}
      <section ref={propertiesRef} className={`py-20 bg-beige-50 slide-up ${propertiesVisible ? 'visible' : ''}`}>
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-thin text-gray-800 mb-4 tracking-wide">
              Tüm İlanlarımız
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto font-light">
              Size en uygun gayrimenkul fırsatlarını keşfedin. 
              Hayalinizdeki mülkü burada bulabilirsiniz.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-600">İlanlar yükleniyor...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {properties.map(property => (
                <PropertyCard key={property._id} property={property} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Hakkımızda Section */}
      <section ref={aboutRef} className={`py-20 bg-white fade-in ${aboutVisible ? 'visible' : ''}`}>
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-thin text-gray-800 mb-4 tracking-wide">
              Hakkımızda
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto font-light">
              10 yıldır emlak sektöründe faaliyet gösteren firmamız, 
              müşteri memnuniyetini ön planda tutarak güvenilir hizmet sunmaktadır.
            </p>
          </div>

          {/* Story Section - Modern Design */}
          <div ref={storyRef} className={`relative mb-20 slide-left ${storyVisible ? 'visible' : ''}`}>
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary-50/30 to-beige-50/30 rounded-3xl"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-primary-100/20 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-beige-100/20 to-transparent rounded-full blur-3xl"></div>
            
            <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/50 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                {/* Content Side */}
                <div className="p-6 lg:p-8">
                  <div className="space-y-5">
                    {/* Header */}
                    <div className="relative">
                      <div className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-primary-600 to-primary-700 rounded-full text-white text-sm font-medium mb-4 shadow-lg">
                        <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>
                        Hikayemiz
                      </div>
                      <h3 className="text-2xl lg:text-3xl font-light text-gray-900 mb-3 leading-tight">
                        Güven ve Deneyimin
                        <span className="block text-primary-700 font-medium">Buluşma Noktası</span>
                      </h3>
                    </div>

                    {/* Story Content with Modern Cards */}
                    <div className="space-y-4">
                      <div className="bg-gradient-to-r from-white to-gray-50/50 rounded-xl p-4 border border-gray-100/50 shadow-sm hover:shadow-md transition-all duration-300">
                        <p className="text-gray-700 leading-relaxed text-sm">
                          <span className="text-primary-700 font-semibold">ORGANİZE GAYRİMENKUL</span>, Gaziantep'in sanayi üssü konumundaki Şehitkamil Organize Sanayi Bölgesi'nde, Fabrika Satışı-Kiralaması ve Fabrika arsası satışı alanında uzmanlaşmış bir emlak firması olarak faaliyet göstermektedir.
                        </p>
                      </div>

                      <div className="bg-gradient-to-r from-primary-50/50 to-white rounded-xl p-4 border border-primary-100/50 shadow-sm hover:shadow-md transition-all duration-300">
                        <p className="text-gray-700 leading-relaxed text-sm">
                          Kurucumuz <span className="text-primary-700 font-semibold">Ali Cansız</span>'ın sektöre olan ilgisi ve bölgeye dair derin bilgi birikimiyle yola çıkan firmamız, işletmelere en uygun sanayi arsalarını sunmak için güvenilir ve sonuç odaklı bir hizmet anlayışıyla çalışmaktadır.
                        </p>
                      </div>

                      <div className="bg-gradient-to-r from-white to-beige-50/50 rounded-xl p-4 border border-beige-100/50 shadow-sm hover:shadow-md transition-all duration-300">
                        <p className="text-gray-700 leading-relaxed text-sm">
                          Kuruluşumuzdan bu yana üretim gücünün kalbi olan bu bölgede, sanayicilerimizin doğru lokasyonda, doğru yatırım kararları almalarına rehberlik ediyoruz. Geniş portföyümüz, yerel piyasa bilgimiz ve güvenilir hizmet anlayışımızla, işletmelerin büyüme yolculuklarında sağlam adımlar atmalarını sağlıyoruz.
                        </p>
                      </div>

                      <div className="bg-gradient-to-r from-gray-50/50 to-white rounded-xl p-4 border border-gray-100/50 shadow-sm hover:shadow-md transition-all duration-300">
                        <p className="text-gray-700 leading-relaxed text-sm">
                          Her zaman <span className="text-primary-700 font-semibold">şeffaflık, güven ve sürdürülebilir iş ilişkileri</span> prensipleriyle çalışıyor; teknoloji destekli çözümlerimizle süreçleri hızlandırıyor ve müşterilerimize değer katmayı amaçlıyoruz.
                        </p>
                      </div>

                      <div className="bg-gradient-to-r from-primary-50/30 to-beige-50/30 rounded-xl p-4 border border-primary-200/30 shadow-sm">
                        <p className="text-gray-700 leading-relaxed font-medium text-sm">
                          Gaziantep sanayisinin geleceğine katkı sağlamak ve işletmelerin yatırım hedeflerine ulaşmasında yol arkadaşı olmak için buradayız.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Image Side */}
                <div className="relative lg:h-auto min-h-[600px] overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent z-10"></div>
                  
                  {/* Rotating Images */}
                  <div className="relative w-full h-full">
                    {officeImages.map((image, index) => (
                      <img 
                        key={index}
                        src={image} 
                        alt={`Ofis görünümü ${index + 1}`}
                        className={`absolute inset-0 w-full h-full object-cover scale-105 hover:scale-100 transition-all duration-700 ${
                          index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                    ))}
                    
                    {/* Image Indicators */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
                      {officeImages.map((_, index) => (
                        <div
                          key={index}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Floating Elements */}
                  <div className="absolute top-8 right-8 bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-xl z-20">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium text-gray-800">Aktif Hizmet</span>
                    </div>
                  </div>

                  <div className="absolute bottom-8 left-8 bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-xl z-20">
                    <div className="text-center">
                      <div className="text-lg font-bold text-primary-700">OSB</div>
                      <div className="text-xs text-gray-600">Organize Sanayi</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Values */}
          <div ref={valuesRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16 fade-in ${valuesVisible ? 'visible' : ''}`}>
            {values.map((value, index) => {
              const Icon = value.icon
              return (
                <div 
                  key={index} 
                  className={`text-center p-6 bg-white border border-gray-200 hover:border-primary-300 scale-up ${valuesVisible ? 'visible' : ''}`}
                  style={{ transitionDelay: `${index * 0.15}s` }}
                >
                  <div className="w-16 h-16 mx-auto mb-4 bg-beige-100 flex items-center justify-center">
                    <Icon className="text-primary-600" size={24} />
                  </div>
                  <h3 className="text-xl font-light text-gray-800 mb-3 tracking-wide">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed font-light">
                    {value.description}
                  </p>
                </div>
              )
            })}
          </div>

          {/* Stats */}
          <div ref={statsRef} className={`bg-white p-12 text-center border-2 border-primary-600 slide-up ${statsVisible ? 'visible' : ''}`}>
            <h3 className="text-3xl font-thin mb-8 tracking-wide text-gray-800">
              Rakamlarla Başarımız
            </h3>
            <div className="grid grid-cols-3 gap-8">
              <div className="p-6">
                <div className="text-4xl font-thin mb-2 text-primary-700">1000+</div>
                <div className="text-gray-600 font-light">Satılan Emlak</div>
              </div>
              <div className="p-6">
                <div className="text-4xl font-thin mb-2 text-primary-700">500+</div>
                <div className="text-gray-600 font-light">Mutlu Müşteri</div>
              </div>
              <div className="p-6">
                <div className="text-4xl font-thin mb-2 text-primary-700">10+</div>
                <div className="text-gray-600 font-light">Yıl Deneyim</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* İletişim Section */}
      <section ref={contactRef} className={`py-20 bg-beige-50 fade-in ${contactVisible ? 'visible' : ''}`}>
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-thin text-gray-800 mb-4 tracking-wide">
              İletişim Bilgileri
          </h2>
          </div>

          <div className="w-full">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-4">
              </div>
              
              <div className="p-8 lg:p-12">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {contactInfo.map((info, index) => {
                    const Icon = info.icon
                    return (
                      <div 
                        key={index} 
                        className={`group relative bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-100 hover:border-primary-300 hover:shadow-lg transform hover:-translate-y-1 slide-up ${contactVisible ? 'visible' : ''}`}
                        style={{ transitionDelay: `${index * 0.1}s` }}
                      >
                        <div className="absolute top-4 right-4 opacity-20 group-hover:opacity-30 transition-opacity">
                          <Icon size={40} className="text-primary-600" />
                        </div>
                        
                        <div className="relative z-10">
                          <div className="bg-primary-100 rounded-full p-4 w-16 h-16 flex items-center justify-center mb-4 group-hover:bg-primary-200 transition-colors">
                            <Icon className="text-primary-600" size={24} />
                          </div>
                          
                          <h4 className="text-lg font-semibold text-gray-800 mb-3 tracking-wide">
                            {info.title}
                          </h4>
                          
                          <div className="space-y-1 mb-3">
                            {info.details.map((detail, idx) => (
                              <p key={idx} className="text-gray-700 font-medium text-sm">
                                {detail}
                              </p>
                            ))}
                          </div>
                          
                          <p className="text-xs text-gray-500 font-light leading-relaxed">
                            {info.description}
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Property Modal */}
      <PropertyModal />
    </div>
  )
}

export default HomePage